import JSONModel from "sap/ui/model/json/JSONModel";
import BaseController from "./BaseController";
import { Besuchsbericht, CENTRY } from "../model/models";
import UI5Date from "sap/ui/core/date/UI5Date";
import { SinglePlanningCalendar$AppointmentSelectEvent } from "sap/m/SinglePlanningCalendar";
import ResponsivePopover from "sap/m/ResponsivePopover";
import { Button$PressEvent } from "sap/m/Button";
import { Route$PatternMatchedEvent } from "sap/ui/core/routing/Route";
import { EnvironmentService } from "../service/Environment.service";
import DateFormat from "sap/ui/core/format/DateFormat";
import { ListItemBase$PressEvent } from "sap/m/ListItemBase";
import { SearchField$SearchEvent } from "sap/m/SearchField";
import { AuthService } from "../service/Auth.service";
import { ErrorHandler } from "../service/ErrorHandler.service";

/**
 * @namespace com.unitrade.crm.controller
 */
export default class AppointmentsController extends BaseController {
  private _oModel: JSONModel;
  private _oAppointmentsModel: JSONModel;
  private _oFilterModel: JSONModel;
  private _oStatusModel: JSONModel;

  private _authService: AuthService;

  private _oResponsiveDetailsPopover: ResponsivePopover;
  private _oResponsiveFilterPopover: ResponsivePopover;

  public override async getTitle(): Promise<string> {
    return await this.getTranslatedString("Calendar");
  }

  onInit(): void {
    this._authService = new AuthService();

    this._oModel = new JSONModel({
      agendaPressed: false,
      busy: false,
    });
    this._oAppointmentsModel = new JSONModel();
    this._oFilterModel = new JSONModel({
      text: "",
      onlyWithMeAsParticipant: true,
      onlyWrittenByMe: false,
      status: "-1",
      start: new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
      end: this.addMonths(new Date(), 1),
    });
    this._oStatusModel = new JSONModel();
    this.setModel(this._oModel);
    this.setModel(this._oAppointmentsModel, "appointments");
    this.setModel(this._oFilterModel, "filters");
    this.setModel(this._oStatusModel, "status");

    this.getRouter().getRoute("appointments").attachPatternMatched(this._onPatternMatch, this);
  }

  private _onPatternMatch = async (oEvent: Route$PatternMatchedEvent) => {
    this._loadAppointments();
  };

  private _loadAppointments = async () => {
    const filters = this._oFilterModel.getData();
    const me = this._authService.getLoggedInUser();
    const parameters: Record<string, string> = {};

    if (filters.text && filters.text.length > 0) {
      parameters["text"] = filters.text;
    }

    if (filters.onlyWithMeAsParticipant) {
      parameters["teilnehmer"] = me.Username;
    }

    if (filters.onlyWrittenByMe) {
      parameters["mitarbeiter"] = me.Username;
    }

    if (filters.start) {
      parameters["von"] = filters.start.toISOString().split("T")[0];
    }

    if (filters.end) {
      parameters["bis"] = filters.end.toISOString().split("T")[0];
    }

    if (filters.status !== "-1") {
      parameters["status"] = filters.status;
    }

    const queryParams = new URLSearchParams(parameters).toString();

    const dt = DateFormat.getDateTimeInstance({ pattern: "dd/MM/yyyy" });
    try {
      this._oModel.setProperty("/busy", true);
      const data = await $.getJSON(`${EnvironmentService.getBaseUrl()}/appointments?${queryParams}`, (obj: Besuchsbericht[]) => {
        obj = obj.map((d: Besuchsbericht) => {
          d.besuchAm = dt.parse(d.besuchAm as string) as UI5Date;
          d.besuchBis = dt.parse(d.besuchBis as string) as UI5Date;
          return d;
        });

        return obj;
      });
      this._oAppointmentsModel.setData(data);
    } catch (ex: any) {
      ErrorHandler.handleError(ex, { onRetry: this._loadAppointments, ignorable: true });
    } finally {
      this._oModel.setProperty("/busy", false);
    }
  };

  public onSearch = async (oEvent: SearchField$SearchEvent) => {
    this._loadAppointments();
  };

  public onAppointmentSelect = async (oEvent: SinglePlanningCalendar$AppointmentSelectEvent) => {
    const appointment = oEvent.getParameter("appointment");
    if (appointment === undefined) {
      return;
    }
    this._oResponsiveDetailsPopover = <ResponsivePopover>await this.getFragment("AppointmentDetails", { addDependant: true });
    const context = appointment.getBindingContext("appointments");
    this._oResponsiveDetailsPopover.setBindingContext(context, "appointments");
    this._oResponsiveDetailsPopover.openBy(appointment.getDomRef() as HTMLElement);
  };

  public onAppointmentPress = async (oEvent: ListItemBase$PressEvent) => {
    this._oResponsiveDetailsPopover = <ResponsivePopover>await this.getFragment("AppointmentDetails", { addDependant: true });
    const context = oEvent.getSource().getBindingContext("appointments");
    this._oResponsiveDetailsPopover.setBindingContext(context, "appointments");
    this._oResponsiveDetailsPopover.openBy(oEvent.getSource().getDomRef() as HTMLElement);
  };

  private showDetailsDialog = async (appointment: Besuchsbericht) => {};

  public onFilterButtonPress = async (oEvent: Button$PressEvent) => {
    this._oResponsiveFilterPopover = <ResponsivePopover>await this.getFragment("AppointmentFilters", { addDependant: true });
    this._oResponsiveFilterPopover.openBy(oEvent.getSource());
    await this.loadBesuchsberichtStatus();
  };

  // ----------------
  // ApointmentFilter.fragment start
  // ----------------
  public onAppointmentFilterUpdate = async (oEvent: Button$PressEvent) => {
    await this._loadAppointments();
    if (this._oResponsiveFilterPopover) {
      this._oResponsiveFilterPopover.close();
    }
  };

  public loadBesuchsberichtStatus = async () => {
    const data: CENTRY[] = await $.getJSON(`${EnvironmentService.getBaseUrl()}/besuchsbericht/status`);
    data.splice(0, 0, { CENTRY_CODE: "-1", CENTRY_DESC: "Alle", CENTRY_ORDER: -10 });
    this._oStatusModel.setData(data);
  };
  // ----------------
  // ApointmentFilter.fragment end
  // ----------------

  // ----------------
  // ApointmentDetails.fragment start
  // ----------------
  public onAppointmentDetailsOpenButton = (oEvent: Button$PressEvent) => {
    const context = oEvent.getSource().getBindingContext("appointments");
    if (context === undefined) return;
    const appointment = <Besuchsbericht>context.getObject();

    this.getOwnerComponent().getRouter().navTo("visitReport", { idPath: appointment.id });
  };

  public onAppointmentDetailsCloseButton = async (oEvent: Button$PressEvent) => {
    if (this._oResponsiveDetailsPopover) {
      this._oResponsiveDetailsPopover.close();
    }
  };
  // ----------------
  // ApointmentDetails.fragment end
  // ----------------

  private addMonths(date: Date, months: number) {
    let d = date.getDate();
    date.setMonth(date.getMonth() + +months);
    if (date.getDate() != d) {
      date.setDate(0);
    }
    return date;
  }
}
